import { useState, useEffect, useRef } from 'react';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import { SparklesIcon, PaperAirplaneIcon, ChevronDownIcon, ChevronUpIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import toast from 'react-hot-toast';

type CarrierInvitation = {
  carrier_id: number;
  carrier_name: string;
  status?: string;
  offer_price?: string;
  offer_currency?: string;
  vehicle_type?: string;
  delivery_eta?: string;
};

type Message = {
  id: number;
  transport_id?: number;
  carrier_id?: number;
  direction: 'outgoing' | 'incoming';
  sender_email?: string;
  recipient_email?: string;
  subject?: string;
  body?: string;
  sent_at?: string;
  created_at?: string;
};

type Order = {
  order_number: string;
  product_short_description?: string;
  ref_name?: string;
};

type Package = {
  length: number;
  width: number;
  height: number;
  weight: number;
  quantity: number;
  package_type?: string;
};

type Transport = {
  id: number;
  reference?: string;
  status: string;
  client_email?: string;
  client_name?: string;
  client_surname?: string;
  client_company?: string;
  carrier_invitations?: CarrierInvitation[];
  orders?: Order[];
  packages?: Package[];
};

interface OfferGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  transport: Transport | null;
  messages: Message[];
  onSuccess: () => void;
}

type CarrierOption = {
  carrierId: number;
  carrierName: string;
  messageCount: number;
  hasOffer: boolean;
  offerPrice?: string;
  offerCurrency?: string;
};

export const OfferGeneratorModal = ({
  isOpen,
  onClose,
  transport,
  messages,
  onSuccess
}: OfferGeneratorModalProps) => {
  const [selectedCarriers, setSelectedCarriers] = useState<number[]>([]);
  const [expandedCarriers, setExpandedCarriers] = useState<number[]>([]);
  const [language, setLanguage] = useState<'pl' | 'en'>('pl');
  const [generatedHtml, setGeneratedHtml] = useState('');
  const [generatedPlain, setGeneratedPlain] = useState('');
  const [subject, setSubject] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState<'select' | 'preview'>('select');
  const [clientEmail, setClientEmail] = useState('');
  const [clientName, setClientName] = useState('');
  const [savingClientInfo, setSavingClientInfo] = useState(false);
  const editableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && transport) {
      // Reset state when modal opens
      setSelectedCarriers([]);
      setLanguage('pl');
      setGeneratedHtml('');
      setGeneratedPlain('');
      setSubject(`Oferta transportowa - Zlecenie #${transport.id}`);
      setStep('select');

      // Clear editable div
      if (editableRef.current) {
        editableRef.current.innerHTML = '';
      }

      // Initialize client data
      setClientEmail(transport.client_email || '');
      const displayName = transport.client_company ||
        `${transport.client_name || ''} ${transport.client_surname || ''}`.trim();
      setClientName(displayName);
    }
  }, [isOpen, transport]);

  // Update editable div content when generatedHtml changes (but only after AI generation)
  useEffect(() => {
    if (editableRef.current && generatedHtml && step === 'preview') {
      // Only update if the div is empty (just after generation)
      if (!editableRef.current.innerHTML || editableRef.current.innerHTML.trim() === '') {
        editableRef.current.innerHTML = generatedHtml;
      }
    }
  }, [generatedHtml, step]);

  if (!transport) return null;

  // Group messages by carrier
  const carrierOptions: CarrierOption[] = [];
  const carrierInvitations = transport.carrier_invitations || [];

  // Create carrier options from invitations
  carrierInvitations.forEach(inv => {
    const carrierMessages = messages.filter(m => m.carrier_id === inv.carrier_id);
    if (carrierMessages.length > 0) {
      carrierOptions.push({
        carrierId: inv.carrier_id,
        carrierName: inv.carrier_name,
        messageCount: carrierMessages.length,
        hasOffer: !!inv.offer_price,
        offerPrice: inv.offer_price,
        offerCurrency: inv.offer_currency || 'PLN'
      });
    }
  });

  const toggleCarrier = (carrierId: number) => {
    setSelectedCarriers(prev => {
      if (prev.includes(carrierId)) {
        return prev.filter(id => id !== carrierId);
      } else {
        return [...prev, carrierId];
      }
    });
  };

  const toggleExpanded = (carrierId: number) => {
    setExpandedCarriers(prev => {
      if (prev.includes(carrierId)) {
        return prev.filter(id => id !== carrierId);
      } else {
        return [...prev, carrierId];
      }
    });
  };

  // Get messages for a specific carrier
  const getCarrierMessages = (carrierId: number) => {
    return messages
      .filter(m => m.carrier_id === carrierId)
      .sort((a, b) => {
        const dateA = new Date(a.sent_at || a.created_at || 0).getTime();
        const dateB = new Date(b.sent_at || b.created_at || 0).getTime();
        return dateA - dateB; // Chronological order
      });
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleGenerate = async () => {
    if (selectedCarriers.length === 0) {
      toast.error('Wybierz co najmniej jedną konwersację');
      return;
    }

    try {
      setGenerating(true);
      const response = await api.post(`/transport/${transport.id}/generate-client-offer`, {
        selectedCarrierIds: selectedCarriers,
        language
      });

      setGeneratedHtml(response.data.html);
      setGeneratedPlain(response.data.plain);

      // Set default subject with [CID:XX] for thread tracking
      if (response.data.defaultSubject) {
        setSubject(response.data.defaultSubject);
      }

      setStep('preview');

      // Show warning if multiple carriers selected (CID tracking won't work)
      if (selectedCarriers.length > 1) {
        toast.success('Oferta wygenerowana. UWAGA: Wybrano wiele przewoźników - wątek nie będzie automatycznie śledzony.', { duration: 6000 });
      } else {
        toast.success('Oferta wygenerowana przez AI');
      }
    } catch (error: any) {
      console.error('Failed to generate offer:', error);
      toast.error(error?.response?.data?.error || 'Nie udało się wygenerować oferty');
    } finally {
      setGenerating(false);
    }
  };

  const saveClientInfo = async () => {
    if (!clientEmail || !clientEmail.trim()) {
      toast.error('Uzupełnij adres email klienta');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(clientEmail)) {
      toast.error('Nieprawidłowy format adresu email');
      return;
    }

    try {
      setSavingClientInfo(true);
      const response = await api.patch(`/transport/${transport.id}/client-info`, {
        clientEmail: clientEmail.trim(),
        clientName: clientName.trim()
      });

      if (response.data.success) {
        toast.success(response.data.message || 'Dane klienta zapisane');
        // Update transport object with new data
        if (transport) {
          transport.client_email = response.data.transport.client_email;
          transport.client_company = response.data.transport.client_company;
        }
      }
    } catch (error: any) {
      console.error('Failed to save client info:', error);
      const errorData = error?.response?.data;
      toast.error(errorData?.message || errorData?.error || 'Nie udało się zapisać danych klienta');
    } finally {
      setSavingClientInfo(false);
    }
  };

  const handleSend = async () => {
    if (!generatedHtml || !subject.trim()) {
      toast.error('Uzupełnij temat wiadomości');
      return;
    }

    // Validate carrier_id is present (required for thread tracking)
    if (selectedCarriers.length !== 1) {
      toast.error('Musisz wybrać dokładnie jednego przewoźnika, aby wysłać ofertę z śledzeniem wątku');
      return;
    }

    const carrierId = selectedCarriers[0];

    try {
      setSending(true);
      const response = await api.post(`/transport/${transport.id}/send-client-offer`, {
        subject: subject.trim(),
        body: generatedHtml,
        attachments: [],
        carrier_id: carrierId // Required for thread tracking with [CID:XX]
      });

      if (response.data.success) {
        toast.success(response.data.message || 'Oferta wysłana pomyślnie!');
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error('Failed to send offer:', error);
      const errorData = error?.response?.data;

      if (errorData?.error === 'Invalid client email format') {
        toast.error(
          `Nieprawidłowy email klienta: ${errorData.email}\n\n${errorData.message}`,
          { duration: 6000 }
        );
      } else if (errorData?.error === 'Client email not found') {
        toast.error(errorData.message, { duration: 5000 });
      } else if (errorData?.error === 'Email sending failed' && errorData?.saved) {
        toast.error(
          `${errorData.message}\nSzczegóły: ${errorData.details}`,
          { duration: 5000 }
        );
      } else {
        toast.error(
          errorData?.message || errorData?.error || 'Nie udało się wysłać oferty',
          { duration: 4000 }
        );
      }
    } finally {
      setSending(false);
    }
  };

  const clientDisplayName = transport.client_company ||
    `${transport.client_name || ''} ${transport.client_surname || ''}`.trim() ||
    transport.client_email ||
    'Klient';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Generator oferty dla klienta"
      size="full"
    >
      <div className="space-y-6 h-[80vh] overflow-y-auto">
        {step === 'select' ? (
          /* STEP 1: SELECT CARRIERS & LANGUAGE */
          <>
            {/* Client Info Edit - MOVED TO STEP 1 */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded space-y-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-gray-900">Dane klienta (edytuj przed generowaniem):</div>
                <Button
                  onClick={saveClientInfo}
                  disabled={savingClientInfo || !clientEmail.trim()}
                  className="bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-1.5"
                >
                  {savingClientInfo ? 'Zapisywanie...' : 'Zapisz do bazy'}
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Client Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nazwa klienta / firma:
                  </label>
                  <input
                    type="text"
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nazwa klienta lub firma"
                  />
                </div>

                {/* Client Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email klienta:
                  </label>
                  <input
                    type="email"
                    value={clientEmail}
                    onChange={(e) => setClientEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="email@example.com"
                  />
                </div>
              </div>

              <p className="text-xs text-gray-600 mt-2">
                Jeśli widzisz błąd w danych klienta, możesz je wyedytować i kliknąć <strong>"Zapisz do bazy"</strong> przed wygenerowaniem oferty
              </p>
            </div>

            {/* Transport Details - Products, Packages, Carriers */}
            <div className="grid grid-cols-3 gap-4">
              {/* Products */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-3 text-sm">
                  Produkty w zamówieniu:
                </h3>
                {transport.orders && transport.orders.length > 0 ? (
                  <ul className="space-y-2.5">
                    {transport.orders.map((order: any, idx: number) => (
                      <li key={idx} className="flex gap-2.5 text-xs">
                        <span className="text-blue-600 mt-0.5 font-bold">•</span>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{order.order_number}</div>
                          {order.product_short_description && (
                            <div className="text-gray-600 mt-0.5 leading-relaxed">
                              {order.product_short_description}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-gray-500 italic">Brak produktów</p>
                )}
              </div>

              {/* Packages */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-3 text-sm">
                  Wymiary paczek:
                </h3>
                {transport.packages && transport.packages.length > 0 ? (
                  <ul className="space-y-2.5">
                    {transport.packages.map((pkg: any, idx: number) => (
                      <li key={idx} className="flex gap-2.5 text-xs">
                        <span className="text-blue-600 mt-0.5 font-bold">•</span>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">Paczka {idx + 1}</div>
                          <div className="text-gray-600 mt-0.5 leading-relaxed">
                            {pkg.length}×{pkg.width}×{pkg.height} cm, {pkg.weight} kg
                            {pkg.quantity > 1 && <span className="ml-1">(ilość: {pkg.quantity})</span>}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-gray-500 italic">Brak paczek</p>
                )}
              </div>

              {/* Carriers Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-3 text-sm">
                  Przewoźnicy:
                </h3>
                {carrierOptions.length > 0 ? (
                  <ul className="space-y-2.5">
                    {carrierOptions.slice(0, 5).map((carrier) => (
                      <li key={carrier.carrierId} className="flex gap-2.5 text-xs">
                        <span className="text-blue-600 mt-0.5 font-bold">•</span>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-900">{carrier.carrierName}</div>
                          {carrier.hasOffer && (
                            <div className="text-green-700 mt-0.5 leading-relaxed font-medium">
                              {carrier.offerPrice} {carrier.offerCurrency}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                    {carrierOptions.length > 5 && (
                      <li className="text-xs text-gray-500 italic ml-5">
                        + {carrierOptions.length - 5} więcej
                      </li>
                    )}
                  </ul>
                ) : (
                  <p className="text-xs text-gray-500 italic">Brak przewoźników</p>
                )}
              </div>
            </div>

            {/* Carrier Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Wybierz konwersacje z przewoźnikami do uwzględnienia w ofercie:
              </label>
              {carrierOptions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Brak dostępnych konwersacji z przewoźnikami</p>
                  <p className="text-sm mt-1">Najpierw wyślij zapytania RFQ i otrzymaj odpowiedzi</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {carrierOptions.map(carrier => {
                    const isExpanded = expandedCarriers.includes(carrier.carrierId);
                    const carrierMessages = getCarrierMessages(carrier.carrierId);

                    return (
                      <div
                        key={carrier.carrierId}
                        className={`border-2 rounded-lg transition-colors ${
                          selectedCarriers.includes(carrier.carrierId)
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200'
                        }`}
                      >
                        {/* Carrier Header */}
                        <div className="flex items-start gap-3 p-4">
                          <input
                            type="checkbox"
                            checked={selectedCarriers.includes(carrier.carrierId)}
                            onChange={() => toggleCarrier(carrier.carrierId)}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-semibold text-gray-900">{carrier.carrierName}</div>
                                <div className="text-sm text-gray-600 mt-1">
                                  Liczba wiadomości: {carrier.messageCount}
                                </div>
                                {carrier.hasOffer && (
                                  <div className="text-sm text-green-700 font-medium mt-1">
                                    Oferta: {carrier.offerPrice} {carrier.offerCurrency}
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={() => toggleExpanded(carrier.carrierId)}
                                className="ml-4 p-2 hover:bg-gray-100 rounded-md transition-colors"
                                title={isExpanded ? 'Zwiń konwersację' : 'Rozwiń konwersację'}
                              >
                                {isExpanded ? (
                                  <ChevronUpIcon className="w-5 h-5 text-gray-600" />
                                ) : (
                                  <ChevronDownIcon className="w-5 h-5 text-gray-600" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Conversation Preview */}
                        {isExpanded && (
                          <div className="border-t border-gray-200 p-4 bg-gray-50">
                            <div className="text-sm font-medium text-gray-700 mb-3">
                              Podgląd konwersacji:
                            </div>
                            <div className="space-y-3">
                              {carrierMessages.map((msg) => (
                                <div
                                  key={msg.id}
                                  className={`p-3 rounded-lg shadow-sm ${
                                    msg.direction === 'outgoing'
                                      ? 'bg-blue-50 border-l-4 border-blue-500'
                                      : 'bg-green-50 border-l-4 border-green-500'
                                  }`}
                                >
                                  {/* Message Header */}
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <EnvelopeIcon className={`w-4 h-4 ${
                                        msg.direction === 'outgoing' ? 'text-blue-600' : 'text-green-600'
                                      }`} />
                                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                        msg.direction === 'outgoing'
                                          ? 'bg-blue-100 text-blue-700'
                                          : 'bg-green-100 text-green-700'
                                      }`}>
                                        {msg.direction === 'outgoing' ? 'Wysłana' : 'Otrzymana'}
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {new Date(msg.sent_at || msg.created_at || '').toLocaleString('pl-PL', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </div>
                                  </div>

                                  {/* Subject */}
                                  <div className="font-medium text-sm text-gray-900 mb-1">
                                    {msg.subject || '(Brak tematu)'}
                                  </div>

                                  {/* From/To */}
                                  <div className="text-xs text-gray-600 mb-2">
                                    <span className="font-medium">
                                      {msg.direction === 'outgoing' ? 'Do: ' : 'Od: '}
                                    </span>
                                    {msg.direction === 'outgoing'
                                      ? msg.recipient_email
                                      : msg.sender_email}
                                  </div>

                                  {/* Body */}
                                  <div
                                    className="prose prose-sm max-w-none text-gray-800 text-xs"
                                    dangerouslySetInnerHTML={{
                                      __html: msg.body || '<p class="text-gray-500 italic">Brak treści</p>'
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Language Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Język oferty:
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="language"
                    value="pl"
                    checked={language === 'pl'}
                    onChange={(e) => setLanguage(e.target.value as 'pl' | 'en')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">Polski</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="language"
                    value="en"
                    checked={language === 'en'}
                    onChange={(e) => setLanguage(e.target.value as 'pl' | 'en')}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">English</span>
                </label>
              </div>
            </div>

            {/* Generate Button */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={generating}
              >
                Anuluj
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={generating || selectedCarriers.length === 0}
                className="bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-2"
              >
                <SparklesIcon className="w-5 h-5" />
                {generating ? 'Generowanie...' : 'Wygeneruj ofertę z AI'}
              </Button>
            </div>
          </>
        ) : (
          /* STEP 2: PREVIEW & SEND */
          <>
            {/* Subject Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Temat wiadomości:
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Temat wiadomości"
              />
            </div>

            {/* Editable Offer Preview */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Podgląd i edycja oferty (kliknij w tekst aby edytować):
              </label>
              <div className="border-2 border-blue-300 rounded-lg overflow-hidden shadow-lg bg-blue-50">
                <div
                  ref={editableRef}
                  className="bg-white max-h-[500px] overflow-y-auto cursor-text hover:bg-blue-50 transition-colors p-4"
                  contentEditable={true}
                  suppressContentEditableWarning={true}
                  onInput={(e) => {
                    setGeneratedHtml(e.currentTarget.innerHTML);
                  }}
                  onFocus={(e) => {
                    e.currentTarget.parentElement?.classList.add('ring-2', 'ring-blue-500');
                  }}
                  onBlur={(e) => {
                    e.currentTarget.parentElement?.classList.remove('ring-2', 'ring-blue-500');
                  }}
                  style={{ outline: 'none', minHeight: '200px' }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Kliknij w dowolne miejsce oferty aby edytować tekst na żywo
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setStep('select')}
                disabled={sending}
              >
                Wstecz
              </Button>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={sending}
                >
                  Anuluj
                </Button>
                <Button
                  onClick={handleSend}
                  disabled={sending || !generatedHtml || !subject.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
                >
                  <PaperAirplaneIcon className="w-5 h-5" />
                  {sending ? 'Wysyłanie...' : 'Wyślij do klienta'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
